import React, { useState } from 'react';
import type { LanguageCode } from '../../../../shared/types';
import { useAvailableLanguages, useCurrentLanguage, useSwitchLanguage } from '../../hooks/useLanguage.js';
import { Modal, ConfirmModal } from '../common/Modal.js';
import { LoadingSpinner } from '../common/LoadingSpinner.js';
import { Button } from '../common/Button.js';

interface LanguageSwitcherProps {
  /** Whether to show as dropdown or button that opens modal */
  variant?: 'dropdown' | 'button';
  /** Show language name or just flag */
  showName?: boolean;
}

export function LanguageSwitcher({ variant = 'dropdown', showName = true }: LanguageSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingLanguage, setPendingLanguage] = useState<LanguageCode | null>(null);

  const currentLanguage = useCurrentLanguage();
  const { languages, isLoading } = useAvailableLanguages();
  const { switchLanguage, isLoading: isSwitching } = useSwitchLanguage();

  const currentLang = languages.find((lang) => lang.code === currentLanguage);

  const handleLanguageClick = (languageCode: LanguageCode) => {
    if (languageCode === currentLanguage) {
      setIsOpen(false);
      return;
    }

    setPendingLanguage(languageCode);
    setShowConfirmation(true);
    setIsOpen(false);
  };

  const handleConfirmSwitch = async () => {
    if (!pendingLanguage) return;

    try {
      await switchLanguage(pendingLanguage);
      setPendingLanguage(null);
      // Reload page to clear cached language-specific data
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch language:', error);
      alert('Failed to switch language. Please try again.');
    }
  };

  if (isLoading || !currentLang) {
    return (
      <div className="flex items-center space-x-2">
        <LoadingSpinner />
      </div>
    );
  }

  const pendingLang = languages.find((lang) => lang.code === pendingLanguage);

  return (
    <>
      {variant === 'dropdown' ? (
        <LanguageDropdown
          currentLang={currentLang}
          languages={languages}
          showName={showName}
          isOpen={isOpen}
          onToggle={() => setIsOpen(!isOpen)}
          onLanguageClick={handleLanguageClick}
        />
      ) : (
        <Button variant="outline" onClick={() => setIsOpen(true)}>
          <span className="mr-2">{currentLang.flag}</span>
          {showName && <span>{currentLang.name}</span>}
        </Button>
      )}

      {/* Language selection modal (for button variant) */}
      {variant === 'button' && (
        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Select Language" size="md">
          <div className="space-y-2">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageClick(lang.code)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition ${
                  lang.code === currentLanguage
                    ? 'border-primary-600 bg-primary-50'
                    : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{lang.flag}</span>
                  <div className="text-left">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {lang.name}
                      {!lang.enabled && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                          Beta
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">{lang.nativeName}</div>
                  </div>
                </div>
                {lang.code === currentLanguage && (
                  <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Confirmation dialog */}
      <ConfirmModal
        isOpen={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setPendingLanguage(null);
        }}
        onConfirm={handleConfirmSwitch}
        title="Switch Language?"
        message={
          pendingLang
            ? `You are about to switch from ${currentLang.name} to ${pendingLang.name}. Your progress in ${currentLang.name} will be saved, but the app will reload with ${pendingLang.name} content.`
            : 'Are you sure you want to switch languages?'
        }
        confirmText={isSwitching ? 'Switching...' : 'Switch Language'}
        cancelText="Cancel"
        variant="primary"
      />
    </>
  );
}

interface LanguageDropdownProps {
  currentLang: { code: LanguageCode; name: string; flag: string; nativeName: string };
  languages: Array<{ code: LanguageCode; name: string; flag: string; nativeName: string; enabled: boolean }>;
  showName: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onLanguageClick: (code: LanguageCode) => void;
}

function LanguageDropdown({
  currentLang,
  languages,
  showName,
  isOpen,
  onToggle,
  onLanguageClick,
}: LanguageDropdownProps) {
  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        onClick={onToggle}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-primary-500"
        aria-label="Select language"
        aria-expanded={isOpen}
      >
        <span className="text-xl">{currentLang.flag}</span>
        {showName && <span className="text-sm font-medium text-gray-700">{currentLang.name}</span>}
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={onToggle} />

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
            <div className="py-1">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => onLanguageClick(lang.code)}
                  className={`w-full flex items-center justify-between px-4 py-3 transition ${
                    lang.code === currentLang.code
                      ? 'bg-primary-50 text-primary-700'
                      : 'hover:bg-gray-50 text-gray-900'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{lang.flag}</span>
                    <div className="text-left">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {lang.name}
                        {!lang.enabled && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                            Beta
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600">{lang.nativeName}</div>
                    </div>
                  </div>
                  {lang.code === currentLang.code && (
                    <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
